import { isUuid } from '@/lib/uuid';
const COMPACT_UUID_PATTERN = /^[A-Za-z0-9_-]{22}$/u;

export interface ParsedUuidUrlValue {
  readonly uuid: string;
  readonly urlValue: string;
}

function uuidBytes(value: string): Uint8Array {
  if (!isUuid(value)) {
    throw new TypeError('Expected a canonical UUID');
  }

  const hex = value.replaceAll('-', '');
  return Uint8Array.from({ length: 16 }, (_, index) =>
    Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16),
  );
}

function uuidFromBytes(bytes: Uint8Array): string {
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-');
}

function encodeBytes(bytes: Uint8Array): string {
  const binary = String.fromCharCode(...bytes);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
}

function decodeBytes(value: string): Uint8Array | null {
  try {
    const base64 = `${value.replaceAll('-', '+').replaceAll('_', '/')}==`;
    const binary = atob(base64);
    if (binary.length !== 16) return null;
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  } catch {
    return null;
  }
}

/** Encodes all 128 UUID bits as an unpadded, URL-safe 22-character value. */
export function encodeUuidForUrl(value: string): string {
  return encodeBytes(uuidBytes(value));
}

/** Decodes only canonical, unpadded compact browser IDs. */
export function parseUuidUrlValue(value: string): ParsedUuidUrlValue | null {
  if (!COMPACT_UUID_PATTERN.test(value)) return null;
  const bytes = decodeBytes(value);
  if (bytes === null || encodeBytes(bytes) !== value) return null;

  return Object.freeze({
    uuid: uuidFromBytes(bytes),
    urlValue: value,
  });
}
